import { CommonModule } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  NO_ERRORS_SCHEMA,
  NgModule,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { PrimeNgModule } from '@g99-product-web-ui/prime-ng';
import { SharedSkeletonLibModule } from '@g99-product-web-ui/shared-skeleton-lib';
import {
  CaseSplitPipe,
  DaysAgoPipe,
  FormatHistoryDatePipe,
  SafeHtmlPipe,
  SafeUrlPipe,
} from '@g99-product-web-ui/shared-util-lib';
import { RichTextEditorModule } from 'angular-rich-text-editor';
import { G99UiLibraryModule } from 'g99-ui-library';
import { NgxGoogleAddressAutocompleteModule } from 'ngx-google-address-autocomplete';
import { ImageCropperComponent } from 'ngx-image-cropper';
import { NgxPhoneModule } from 'ngx-phone';
import { CheckboxModule } from 'primeng/checkbox';
import { InputSwitchModule } from 'primeng/inputswitch';

import { AccordionCardComponent } from './components/accordion-card/accordion-card.component';
import { AdvanceTableFilterComponent } from './components/advance-table-filter/advance-table-filter.component';
import { AiPopupOptionsComponent } from './components/ai-popup-options/ai-popup-options.component';
import { AsigneeSingleOptionDrownComponent } from './components/asignee-single-option-drown/asignee-single-option-drown.component';
import { AvatarComponent } from './components/avatar/avatar.component';
import { ChipComponent } from './components/chip/chip.component';
import { ChipTextComponent } from './components/chip-text/chip-text.component';
import { ComingSoonComponent } from './components/coming-soon/coming-soon.component';
import { DateInputComponent } from './components/date-input/date-input.component';
import { DateRangeFilterComponent } from './components/date-range-filter/date-range-filter.component';
import { RichDropdownComponent } from './components/dropdown/rich-dropdown/rich-dropdown.component';
import { EmptyFormArrayComponent } from './components/empty-form-array/empty-form-array.component';
import { ErrorStateComponent } from './components/error-state/error-state.component';
import { FileInputComponent } from './components/file-input/file-input.component';
import { FooterButtonComponent } from './components/footer-button/footer-button.component';
import { FormBuilderInputComponent } from './components/form-builder-input/form-builder-input.component';
import { LibImageCropperComponent } from './components/image-cropper/image-cropper.component';
import { ImageUploadComponent } from './components/image-upload/image-upload.component';
import { InlineEditorComponent } from './components/inline-editor/inline-editor.component';
import { InputComponent } from './components/input/input.component';
import { InputCheckboxComponent } from './components/input-checkbox/input-checkbox.component';
import { InputNumberComponent } from './components/input-number/input-number.component';
import { InputPhoneNumberComponent } from './components/input-phone-number/input-phone-number.component';
import { InputSwitcherComponent } from './components/input-switcher/input-switcher.component';
import { ToggleSwitchWithLoaderComponent } from './components/input-switcher/toggle-switch-with-loader/toggle-switch-with-loader.component';
import { InputTextareaComponent } from './components/input-textarea/input-textarea.component';
import { InputWithCheckboxComponent } from './components/input-with-checkbox/input-with-checkbox.component';
import { InputWithCopyComponent } from './components/input-with-copy/input-with-copy.component';
import { InputWithLabelInsideComponent } from './components/input-with-label-inside/input-with-label-inside.component';
import { InputWithTextComponent } from './components/input-with-text/input-with-text.component';
import { LabelInputComponent } from './components/label-input/label-input.component';
import { LoaderComponent } from './components/loader/loader.component';
import { MessageBoxComponent } from './components/message-box/message-box.component';
import { CommonModalComponent } from './components/modals/common-modal/common-modal.component';
import { DeleteConfirmationModalComponent } from './components/modals/delete-confirmation-modal/delete-confirmation-modal.component';
import { SaveChangesModalComponent } from './components/modals/save-changes-modal/save-changes-modal.component';
import { SharedExportModalComponent } from './components/modals/shared-export-modal/shared-export-modal.component';
import { SharedScriptEmbedModalComponent } from './components/modals/shared-script-embed-modal/shared-script-embed-modal.component';
import { SharedStatusUpdateModalComponent } from './components/modals/shared-status-update-modal/shared-status-update-modal.component';
import { FileUploadSizeWarningComponent } from './components/multi-file-upload/file-upload-size-warning/file-upload-size-warning.component';
import { MultiFileUploadComponent } from './components/multi-file-upload/multi-file-upload.component';
import { UploadedFilePreviewComponent } from './components/multi-file-upload/uploaded-file-preview/uploaded-file-preview.component';
import { MultiSelectDropdownComponent } from './components/multi-select-dropdown/multi-select-dropdown.component';
import { NoRoutingPageComponent } from './components/no-routing-page/no-routing-page.component';
import { PAutocompleteInputComponent } from './components/p-autocomplete-input/p-autocomplete-input.component';
import { PRadioInputComponent } from './components/p-radio-input/p-radio-input.component';
import { AiPageHeaderComponent } from './components/page-header/ai-page-header/ai-page-header.component';
import { PageHeaderComponent } from './components/page-header/page-header.component';
import { PreviewShareButtonComponent } from './components/preview-share-button/preview-share-button.component';
import { RichTextEditorComponent } from './components/rich-text-editor/rich-text-editor.component';
import { SharedAddEditTaskComponent } from './components/shared-add-edit-task/shared-add-edit-task.component';
import { SharedAddEditReviewComponent } from './components/shared-add-review/shared-add-edit-review.component';
import { SharedBreadcrumbComponent } from './components/shared-breadcrumb/shared-breadcrumb.component';
import { SharedDropdownTemplateComponent } from './components/shared-dropdown-template/shared-dropdown-template.component';
import { SharedNotificationConfigurationComponent } from './components/shared-notification-configuration/shared-notification-configuartion.component';
import { SharedPaginatorComponent } from './components/shared-paginator/shared-paginator.component';
import { SharedPopoverComponent } from './components/shared-popover/shared-popover.component';
import { SharedPreviewFileComponent } from './components/shared-preview-file/shared-preview-file.component';
import { SharedSidePopoverComponent } from './components/shared-side-popover/shared-side-popover.component';
import { SharedStatusMessageComponent } from './components/shared-status-message/shared-status-message.component';
import { SharedStepperComponent } from './components/shared-stepper/shared-stepper.component';
import { SharedTabComponent } from './components/shared-tab/shared-tab.component';
import { SharedTableComponent } from './components/shared-table/shared-table.component';
import { SharedTableColumnPreferenceComponent } from './components/shared-table-column-preference/shared-table-column-preference.component';
import { SharedTableFooterActionComponent } from './components/shared-table-footer-action/shared-table-footer-action.component';
import { SharedTableHeaderComponent } from './components/shared-table-header/shared-table-header.component';
import { SharedTaskCardComponent } from './components/shared-task-card/shared-task-card.component';
import { SharedToolTipComponent } from './components/shared-tooltip/shared-tooltip.component';
import { SingleOptionDropdownComponent } from './components/single-option-dropdown/single-option-dropdown.component';
import { StatusStageDropdownComponent } from './components/status-stage-dropdown/status-stage-dropdown.component';
import { StepperFooterButtonComponent } from './components/stepper-footer-button/stepper-footer-button.component';
import { TableActionMenuComponent } from './components/table-action-menu/table-action-menu.component';
import { DateAuthorInfoComponent } from './components/table-date-author/table-date-author.component';
import { TableFieldsPairComponent } from './components/table-fields-pair/table-fields-pair.component';
import { TableMenuComponent } from './components/table-menu/table-menu.component';
import { TableNoResultComponent } from './components/table-no-result/table-no-result.component';
import { TextareaWithButtonComponent } from './components/textarea-with-button/textarea-with-button.component';
import { ThreeColumnOptionsMenuComponent } from './components/three-column-options-table/three-column-options-menu.component';
import { TimeInputComponent } from './components/time-input/time-input.component';
import { ToggleSwitchComponent } from './components/toggle-switch/toggle-switch.component';
import { ClickOutsideDirective } from './directives/click-outside.directive';
import { LazyImgDirective } from './directives/lazy-image-load.directive';
import { GenerateInitialsPipe } from './pipes/generate-initials-pipe';
import { NormalizePhoneNumberPipe } from './pipes/shared-normalize-phone-number.pipe';
@NgModule({
  imports: [
    NgxGoogleAddressAutocompleteModule,
    CommonModule,
    PrimeNgModule,
    FormsModule,
    ReactiveFormsModule,
    G99UiLibraryModule,
    InputSwitchModule,
    CheckboxModule,
    ImageCropperComponent,
    SharedSkeletonLibModule,
    RichTextEditorModule,
    NgxPhoneModule,
  ],
  declarations: [
    CommonModalComponent,
    SharedToolTipComponent,
    SaveChangesModalComponent,
    LoaderComponent,
    InputWithCopyComponent,
    InputWithTextComponent,
    InputSwitcherComponent,
    AccordionCardComponent,
    InputCheckboxComponent,
    InputComponent,
    InputTextareaComponent,
    ImageUploadComponent,
    FooterButtonComponent,
    PageHeaderComponent,
    SharedTableComponent,
    TableMenuComponent,
    AvatarComponent,
    ThreeColumnOptionsMenuComponent,
    DateAuthorInfoComponent,
    DeleteConfirmationModalComponent,
    TextareaWithButtonComponent,
    InputWithCheckboxComponent,
    StepperFooterButtonComponent,
    TableActionMenuComponent,
    SharedTabComponent,
    SharedStepperComponent,
    StepperFooterButtonComponent,
    InputWithLabelInsideComponent,
    LabelInputComponent,
    TableNoResultComponent,
    LabelInputComponent,
    ErrorStateComponent,
    SharedTableHeaderComponent,
    MultiSelectDropdownComponent,
    InputPhoneNumberComponent,
    TableFieldsPairComponent,
    DateInputComponent,
    PAutocompleteInputComponent,
    SingleOptionDropdownComponent,
    PRadioInputComponent,
    RichTextEditorComponent,
    FormBuilderInputComponent,
    EmptyFormArrayComponent,
    LibImageCropperComponent,
    FileInputComponent,
    ChipComponent,
    TimeInputComponent,
    SharedPaginatorComponent,
    MessageBoxComponent,
    NoRoutingPageComponent,
    ChipTextComponent,
    SafeHtmlPipe,
    AsigneeSingleOptionDrownComponent,
    StatusStageDropdownComponent,
    DaysAgoPipe,
    FormatHistoryDatePipe,
    CaseSplitPipe,
    SharedTaskCardComponent,
    SharedAddEditTaskComponent,
    SharedDropdownTemplateComponent,
    SharedSidePopoverComponent,
    MultiFileUploadComponent,
    SharedTableFooterActionComponent,
    SharedExportModalComponent,
    SharedPreviewFileComponent,
    SafeUrlPipe,
    DateRangeFilterComponent,
    SharedStatusUpdateModalComponent,
    GenerateInitialsPipe,
    AdvanceTableFilterComponent,
    InlineEditorComponent,
    SharedStatusMessageComponent,
    RichDropdownComponent,
    ClickOutsideDirective,
    SharedScriptEmbedModalComponent,
    PreviewShareButtonComponent,
    UploadedFilePreviewComponent,
    LazyImgDirective,
    InputNumberComponent,
    SharedTableColumnPreferenceComponent,
    SharedPopoverComponent,
    ComingSoonComponent,
    ToggleSwitchComponent,
    AiPopupOptionsComponent,
    ToggleSwitchWithLoaderComponent,
    FileUploadSizeWarningComponent,
    AiPageHeaderComponent,
    NormalizePhoneNumberPipe,
    SharedBreadcrumbComponent,
    SharedNotificationConfigurationComponent,
    SharedAddEditReviewComponent,
  ],
  exports: [
    CommonModalComponent,
    SharedToolTipComponent,
    SaveChangesModalComponent,
    LoaderComponent,
    InputWithCopyComponent,
    InputWithTextComponent,
    InputSwitcherComponent,
    AccordionCardComponent,
    InputCheckboxComponent,
    InputComponent,
    InputTextareaComponent,
    ImageUploadComponent,
    FooterButtonComponent,
    PageHeaderComponent,
    SharedTableComponent,
    TableMenuComponent,
    AvatarComponent,
    ThreeColumnOptionsMenuComponent,
    DateAuthorInfoComponent,
    DeleteConfirmationModalComponent,
    TextareaWithButtonComponent,
    InputWithCheckboxComponent,
    StepperFooterButtonComponent,
    TableActionMenuComponent,
    SharedTabComponent,
    SharedStepperComponent,
    StepperFooterButtonComponent,
    G99UiLibraryModule,
    InputWithLabelInsideComponent,
    LabelInputComponent,
    TableNoResultComponent,
    ErrorStateComponent,
    SharedTableHeaderComponent,
    SingleOptionDropdownComponent,
    MultiSelectDropdownComponent,
    InputPhoneNumberComponent,
    TableFieldsPairComponent,
    DateInputComponent,
    PAutocompleteInputComponent,
    SingleOptionDropdownComponent,
    PRadioInputComponent,
    RichTextEditorComponent,
    FormBuilderInputComponent,
    EmptyFormArrayComponent,
    LibImageCropperComponent,
    FileInputComponent,
    ChipComponent,
    TimeInputComponent,
    SharedPaginatorComponent,
    MessageBoxComponent,
    NoRoutingPageComponent,
    ChipTextComponent,
    SafeHtmlPipe,
    AsigneeSingleOptionDrownComponent,
    StatusStageDropdownComponent,
    DaysAgoPipe,
    FormatHistoryDatePipe,
    CaseSplitPipe,
    SharedTaskCardComponent,
    SharedAddEditTaskComponent,
    SharedDropdownTemplateComponent,
    SharedSidePopoverComponent,
    MultiFileUploadComponent,
    SharedTableFooterActionComponent,
    SharedExportModalComponent,
    SharedPreviewFileComponent,
    DateRangeFilterComponent,
    SharedStatusUpdateModalComponent,
    GenerateInitialsPipe,
    AdvanceTableFilterComponent,
    InlineEditorComponent,
    SharedStatusMessageComponent,
    RichDropdownComponent,
    SharedScriptEmbedModalComponent,
    PreviewShareButtonComponent,
    LazyImgDirective,
    InputNumberComponent,
    RichTextEditorModule,
    SharedTableColumnPreferenceComponent,
    SharedPopoverComponent,
    ComingSoonComponent,
    ToggleSwitchComponent,
    AiPopupOptionsComponent,
    ToggleSwitchWithLoaderComponent,
    AiPageHeaderComponent,
    NormalizePhoneNumberPipe,
    SharedBreadcrumbComponent,
    SharedNotificationConfigurationComponent,
    SharedAddEditReviewComponent,
  ],
  schemas: [NO_ERRORS_SCHEMA, CUSTOM_ELEMENTS_SCHEMA],
})
export class SharedUiModule {}
